import type { AnyID } from '@types'
import {
  isRelaxIntegratedUnitByDefaults,
  parseRelaxArmVariantCatalog,
} from '@utils/atemporal/relaxArmVariants'
import { resolveLayoutWidth } from '@utils/linkedArms/armPlacement'
import { relaxPositionCoversSide } from '@utils/linkedArms/relaxArmSideAvailability'
import { isRinconeroCatalog, rinconeroPlanFondo } from '@utils/sceneObjects/items/rinconeroUtils'
import { Quaternion, Vector3 } from 'three'
import { allowedArmSideForUnit, unitAllowsOnlyOneArm } from './singleArmSeriesRules'

const isArmCatalog = (catalogID: unknown) => /^BR\d+(-\d+)?$/i.test(String(catalogID || '').trim())

const STRAIGHT_TOLERANCE_MM = 120
const SIDE_TOLERANCE_MM = 80
const SNAP_DEPTH_TOLERANCE_MM = 120
const LEG_CORNER_TOLERANCE_MM = 200

export type RowSide = 1 | -1

export type ArmPlacementSlot = { unit: any; side: RowSide }

const getWorldPos = (item: any) => {
  if (item?.matrix) {
    const pos = new Vector3()
    item.matrix.decompose(pos, new Quaternion(), new Vector3())
    return pos
  }
  return new Vector3(Number(item?.x || 0), Number(item?.y || 0), Number(item?.z || 0))
}

const getUnitXAxis = (unit: any) => {
  const q = new Quaternion()
  unit.matrix.decompose(new Vector3(), q, new Vector3())
  return new Vector3(1, 0, 0).applyQuaternion(q)
}

const getUnitZAxis = (unit: any) => {
  const q = new Quaternion()
  unit.matrix.decompose(new Vector3(), q, new Vector3())
  return new Vector3(0, 0, 1).applyQuaternion(q)
}

const unitPlanWidth = (unit: any) =>
  resolveLayoutWidth(unit) || Number(unit?.width || 0)

const unitPlanFondo = (unit: any) => {
  const thickness = Number(unit?.thickness || 0)
  if (Number.isFinite(thickness) && thickness > 0) return thickness
  return rinconeroPlanFondo(unit) || 0
}

const straightModulesAdjacent = (from: any, to: any) => {
  const along = Math.abs(getWorldPos(to).clone().sub(getWorldPos(from)).dot(getUnitXAxis(from)))
  const fromWidth = unitPlanWidth(from)
  const toWidth = unitPlanWidth(to)
  return along <= fromWidth / 2 + toWidth / 2 + STRAIGHT_TOLERANCE_MM
}

/** Unidad contigua por snap lateral (origen a origen en el borde ±X). */
const snapEdgeAdjacent = (anchor: any, candidate: any) => {
  if (!anchor?.matrix || !candidate?.matrix) return false

  const anchorPos = getWorldPos(anchor)
  const anchorX = getUnitXAxis(anchor)
  const anchorZ = getUnitZAxis(anchor)
  const anchorWidth = unitPlanWidth(anchor)
  const candidateWidth = unitPlanWidth(candidate)

  const delta = getWorldPos(candidate).clone().sub(anchorPos)
  const sideProjection = delta.dot(anchorX)
  const depthProjection = Math.abs(delta.dot(anchorZ))
  if (depthProjection > SNAP_DEPTH_TOLERANCE_MM) return false

  const onRight = Math.abs(sideProjection - anchorWidth) <= SIDE_TOLERANCE_MM
  const onLeft = Math.abs(sideProjection + candidateWidth) <= SIDE_TOLERANCE_MM
  return onRight || onLeft
}

/** Módulo perpendicular en pata de L (rinconero / extremo con brazo). */
const legSnapAdjacent = (anchor: any, candidate: any) => {
  if (!anchor?.matrix || !candidate?.matrix) return false

  const anchorQuat = new Quaternion()
  const candidateQuat = new Quaternion()
  anchor.matrix.decompose(new Vector3(), anchorQuat, new Vector3())
  candidate.matrix.decompose(new Vector3(), candidateQuat, new Vector3())

  const rotDiff = anchorQuat.angleTo(candidateQuat)
  const perpendicular =
    Math.abs(rotDiff - Math.PI / 2) < 0.55 || Math.abs(rotDiff - (3 * Math.PI) / 2) < 0.55
  if (!perpendicular) return false

  const anchorPos = getWorldPos(anchor)
  const candidatePos = getWorldPos(candidate)
  const anchorX = getUnitXAxis(anchor)
  const anchorZ = getUnitZAxis(anchor)
  const anchorWidth = unitPlanWidth(anchor)
  const anchorFondo = unitPlanFondo(anchor)

  const nearCorner = (corner: Vector3) => {
    const rel = candidatePos.clone().sub(corner)
    const alongX = Math.abs(rel.dot(anchorX))
    const alongZ = Math.abs(rel.dot(anchorZ))
    return alongX <= LEG_CORNER_TOLERANCE_MM && alongZ <= anchorFondo + LEG_CORNER_TOLERANCE_MM
  }

  const leftCorner = anchorPos.clone()
  const rightCorner = anchorPos.clone().add(anchorX.clone().multiplyScalar(anchorWidth))
  if (nearCorner(leftCorner) || nearCorner(rightCorner)) return true

  const candidateX = getUnitXAxis(candidate)
  const candidateZ = getUnitZAxis(candidate)
  const candidateWidth = unitPlanWidth(candidate)
  const candidateFondo = unitPlanFondo(candidate)
  const candidateLeft = candidatePos.clone()
  const candidateRight = candidatePos.clone().add(candidateX.clone().multiplyScalar(candidateWidth))

  const nearCandidateCorner = (corner: Vector3) => {
    const rel = anchorPos.clone().sub(corner)
    const alongX = Math.abs(rel.dot(candidateX))
    const alongZ = Math.abs(rel.dot(candidateZ))
    return alongX <= LEG_CORNER_TOLERANCE_MM && alongZ <= candidateFondo + LEG_CORNER_TOLERANCE_MM
  }

  return nearCandidateCorner(candidateLeft) || nearCandidateCorner(candidateRight)
}

/** Lado ±X bloqueado por un vecino en pata perpendicular (rinconero ↔ módulo de L). */
const resolvePerpendicularBlockedSide = (unit: any, other: any): RowSide | null => {
  if (!legSnapAdjacent(unit, other) && !legSnapAdjacent(other, unit)) return null

  const unitPos = getWorldPos(unit)
  const otherPos = getWorldPos(other)
  const unitX = getUnitXAxis(unit)
  const unitWidth = unitPlanWidth(unit)
  const xProj = otherPos.clone().sub(unitPos).dot(unitX)

  if (xProj <= SIDE_TOLERANCE_MM) return -1
  if (xProj >= unitWidth - SIDE_TOLERANCE_MM) return 1
  return xProj < unitWidth / 2 ? -1 : 1
}

const isRinconeroLateralNeighborOnSide = (
  unit: any,
  other: any,
  side: RowSide,
): boolean => {
  if (!isRinconeroCatalog(other?.catalogID) || !other?.matrix || !unit?.matrix) return false
  if (legSnapAdjacent(unit, other) || legSnapAdjacent(other, unit)) return false

  const unitPos = getWorldPos(unit)
  const otherPos = getWorldPos(other)
  const unitX = getUnitXAxis(unit)
  const unitZ = getUnitZAxis(unit)
  const unitWidth = unitPlanWidth(unit)
  const otherWidth = unitPlanWidth(other)

  const delta = otherPos.clone().sub(unitPos)
  const sideProjection = delta.dot(unitX)
  const depthProjection = Math.abs(delta.dot(unitZ))
  const rinconeroDepthBudget = unitPlanFondo(other) + unitPlanFondo(unit) + 180
  if (depthProjection > rinconeroDepthBudget) return false

  if (side === 1) {
    return Math.abs(sideProjection - unitWidth) <= SIDE_TOLERANCE_MM + 60
  }
  return Math.abs(sideProjection + otherWidth) <= SIDE_TOLERANCE_MM + 60
}

const modulesAreAdjacent = (a: any, b: any) => {
  if (!a?.matrix || !b?.matrix) return false
  if (String(a.id) === String(b.id)) return true
  return (
    straightModulesAdjacent(a, b) ||
    straightModulesAdjacent(b, a) ||
    snapEdgeAdjacent(a, b) ||
    snapEdgeAdjacent(b, a) ||
    legSnapAdjacent(a, b) ||
    legSnapAdjacent(b, a)
  )
}

export function getLinkedModulesInRow(items: Map<AnyID, any>, referenceUnit: any): any[] {
  if (!referenceUnit?.id) return []
  const live = items.get(referenceUnit.id as AnyID) as any
  if (!live?.matrix) return []

  const candidates = Array.from(items.values()).filter((it: any) => {
    if (!it || it.type !== 'linkedItem') return false
    if (isArmCatalog(it.catalogID)) return false
    return true
  })

  const row: any[] = [live]
  let grew = true
  while (grew) {
    grew = false
    for (const candidate of candidates) {
      if (row.some((u) => String(u.id) === String(candidate.id))) continue
      if (row.some((u) => modulesAreAdjacent(u, candidate))) {
        row.push(candidate)
        grew = true
      }
    }
  }
  return row
}

export function createArmRowContext(
  items: Map<AnyID, any>,
  referenceUnit: any,
  options?: {
    excludeArmId?: string
    getArmSideSign?: (unit: any, arm: any) => RowSide
  },
) {
  const row = getLinkedModulesInRow(items, referenceUnit)
  const excludeArmId = options?.excludeArmId
  const getArmSideSign = options?.getArmSideSign ?? (() => 1 as RowSide)

  const getArmsOnUnit = (unitId: string) =>
    Array.from(items.values()).filter((it: any) => {
      if (!it || it.type !== 'linkedItem' || !isArmCatalog(it.catalogID)) return false
      if (excludeArmId && String(it.id) === String(excludeArmId)) return false
      return String(it.firstParentID || '') === String(unitId)
    })

  const isNeighborOnSide = (unit: any, side: RowSide) => {
    if (!unit?.matrix) return false
    const unitPos = getWorldPos(unit)
    const unitX = getUnitXAxis(unit)
    const unitZ = getUnitZAxis(unit)
    const unitWidth = unitPlanWidth(unit)

    return row.some((other) => {
      if (String(other.id) === String(unit.id) || !other?.matrix) return false

      const perpendicularSide = resolvePerpendicularBlockedSide(unit, other)
      if (perpendicularSide === side) return true

      if (isRinconeroLateralNeighborOnSide(unit, other, side)) return true

      const otherPos = getWorldPos(other)
      const delta = otherPos.clone().sub(unitPos)
      const sideProjection = delta.dot(unitX)
      const depthProjection = Math.abs(delta.dot(unitZ))
      if (depthProjection > SNAP_DEPTH_TOLERANCE_MM) return false

      const otherWidth = unitPlanWidth(other)
      if (side === 1) {
        return Math.abs(sideProjection - unitWidth) <= SIDE_TOLERANCE_MM
      }
      return Math.abs(sideProjection + otherWidth) <= SIDE_TOLERANCE_MM
    })
  }

  /** RELAX integra el brazo en el GLB (p. ej. «167 BR3 D»); cuenta como ocupación lateral. */
  const hasRelaxIntegratedArmOnSide = (unit: any, side: RowSide) => {
    const parts = parseRelaxArmVariantCatalog(unit?.catalogID)
    if (!parts) return false
    return relaxPositionCoversSide(parts.position, side === 1 ? 'right' : 'left')
  }

  const hasArmOnSide = (unit: any, side: RowSide) =>
    hasRelaxIntegratedArmOnSide(unit, side) ||
    getArmsOnUnit(String(unit.id)).some((arm) => getArmSideSign(unit, arm) === side)

  const getArmCountOnUnit = (unitId: string) => getArmsOnUnit(unitId).length

  const listExteriorLips = (): ArmPlacementSlot[] => {
    const lips: ArmPlacementSlot[] = []
    for (const unit of row) {
      // Rinconero 166-: unión de la L; los brazos van en módulos rectos terminales.
      if (isRinconeroCatalog(unit?.catalogID)) continue
      // RELAX gestiona brazos por variante GLB; nunca es ancla de un BR independiente.
      if (isRelaxIntegratedUnitByDefaults(unit?.catalogID)) continue
      for (const side of [-1, 1] as const) {
        if (!isNeighborOnSide(unit, side)) {
          lips.push({ unit, side })
        }
      }
    }
    return lips
  }

  return {
    row,
    getArmsOnUnit,
    isNeighborOnSide,
    hasArmOnSide,
    getArmCountOnUnit,
    listExteriorLips,
  }
}

/**
 * Lado lateral (±X local) al que se acerca un brazo respecto a una unidad base.
 * Devuelve null si el acercamiento es frontal/trasero (no es un extremo lateral).
 */
export function resolveArmSnapSideFromApproach(
  armWorldPos: Vector3,
  targetUnit: any,
): RowSide | null {
  if (!targetUnit?.matrix) return null
  const unitPos = getWorldPos(targetUnit)
  const unitX = getUnitXAxis(targetUnit)
  const unitZ = getUnitZAxis(targetUnit)
  const delta = armWorldPos.clone().sub(unitPos).setY(0)
  const xProj = delta.dot(unitX)
  const zProj = delta.dot(unitZ)
  if (Math.abs(xProj) < Math.abs(zProj) * 0.45) return null
  if (Math.abs(xProj) < 40) return null
  return xProj >= 0 ? 1 : -1
}

/**
 * Los brazos BR* solo pueden anclarse en un labio exterior de fila:
 * extremo de unidad sin vecino contiguo en ese lado.
 */
export function canArmSnapToExteriorLip(
  items: Map<AnyID, any>,
  targetUnit: any,
  side: RowSide,
  options?: {
    excludeArmId?: string
    getArmSideSign?: (unit: any, arm: any) => RowSide
  },
): boolean {
  if (!targetUnit?.matrix || isArmCatalog(targetUnit.catalogID)) return false
  if (targetUnit.type !== 'linkedItem') return false
  if (isRelaxIntegratedUnitByDefaults(targetUnit.catalogID)) return false

  const ctx = createArmRowContext(items, targetUnit, options)
  if (ctx.isNeighborOnSide(targetUnit, side)) return false

  const handedSide = allowedArmSideForUnit(targetUnit.catalogID)
  if (handedSide != null && side !== handedSide) return false

  if (unitAllowsOnlyOneArm(targetUnit) && ctx.getArmCountOnUnit(String(targetUnit.id)) > 0) {
    return false
  }

  if (ctx.hasArmOnSide(targetUnit, side) && unitAllowsOnlyOneArm(targetUnit)) {
    return false
  }

  return true
}
